// OrderPickupTable.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useLazyGetOrderPickupDetailQuery } from '../../../../api/endpoints/parts';
import { useLanguage } from '../../../../contexts/LanguageContext';

export default function OrderPickupTablePage({
  tableData,
  setTableData,
  warehouses,
  hidePriceColumns = false,
  invoiceNo = '',
  deliveryNoteNo = '',
}) {
  const { th } = useLanguage();
  const [selectAll, setSelectAll] = useState(false);
  const [detailDialogVisible, setDetailDialogVisible] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [fetchDetail, { isFetching: isFetchingDetail }] = useLazyGetOrderPickupDetailQuery();

  const handleShowDetail = async (row) => {
    setDetailDialogVisible(true);
    setDetailData([]);
    try {
      const result = await fetchDetail({
        Matnr: row.partCode || '',
        OrdrNo: row.ordrNo || '',
        KlmNo: row.psnr || '',
        Dln: row.dln || deliveryNoteNo || '',
        InvNo: invoiceNo || ''
      }).unwrap();
      setDetailData(result?.data ?? []);
    } catch {
      setDetailData([]);
    }
  };

  const DetailCell = (row) => {
    const received = Number(row.receivedQuantity ?? 0);
    if (received <= 0) return null;
    return (
      <div className="flex justify-content-center">
        <Button
          icon="pi pi-eye"
          rounded
          text
          severity="info"
          size="small"
          tooltip={th('parts.pickupDetail')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleShowDetail(row)}
        />
      </div>
    );
  };

  // warehouses -> options (value: index), id lookup by index
  const options = useMemo(
    () => (warehouses?.map((w, idx) => ({ label: w.name, value: idx })) ?? []),
    [warehouses]
  );

  const getRemainingQty = (row) => {
    const total = Number(row.totalQuantity ?? 0);
    const received = Number(row.receivedQuantity ?? 0);
    return Math.max(0, total - received);
  };

  // İlk yüklemede/warehouses değiştiğinde warehouseId boş olan satırlara ilk depo id'sini ata
  useEffect(() => {
    if (!warehouses?.length || !tableData?.length) return;
    const firstId = warehouses[0].id;
    let changed = false;

    const fixed = tableData.map(r => {
      if (r.warehouseId == null) {
        changed = true;
        return { ...r, warehouseId: firstId };
      }
      return r;
    });

    if (changed) setTableData(fixed);
  }, [warehouses, tableData, setTableData]);

  useEffect(() => {
    if (!tableData?.length) {
      setSelectAll(false);
      return;
    }

    const selectableRows = tableData.filter((row) => getRemainingQty(row) > 0);
    if (!selectableRows.length) {
      setSelectAll(false);
      return;
    }

    const allSelected = selectableRows.every((row) => Number(row.quantity ?? 0) === getRemainingQty(row));
    setSelectAll(allSelected);
  }, [tableData]);

  const QtyCell = (row) => {
    const maxRemain = getRemainingQty(row);
    const disabled = maxRemain <= 0;

    return (
      <div className="flex justify-content-center w-full">
        <input
          type="number"
          disabled={disabled}
          className="p-inputtext p-inputtext-sm text-center"
          style={{ width: '6rem', height: '2.25rem' }}
          value={row.quantity ?? 0}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              setTableData(prev =>
                prev.map(r => (r.id === row.id ? { ...r, quantity: 0 } : r))
              );
              return;
            }
            let newQty = Number(raw) || 0;
            if (disabled) newQty = 0;
            else {
              if (newQty < 0) newQty = 0;
              if (newQty > maxRemain) newQty = maxRemain;
            }

            setTableData(prev =>
              prev.map(r => (r.id === row.id ? { ...r, quantity: newQty } : r))
            );
          }}
        />
      </div>
    );
  };

  const WarehouseCell = (row) => {
    // Dropdown gösterimi index ile, state kaydı id ile.
    // Seçili index: row.warehouseId -> warehouses.findIndex(...)
    const selectedIndex = useMemo(() => {
      if (!warehouses?.length) return null;
      const idx = warehouses.findIndex(w => w.id === row.warehouseId);
      return idx >= 0 ? idx : 0; // bulunamazsa 0 (ilk eleman)
    }, [row.warehouseId, warehouses]);

    return (
      <div className="flex justify-content-center w-full">
        <Dropdown
          value={selectedIndex}
          options={options}
          onChange={(e) => {
            const wh = warehouses?.[e.value];
            if (!wh) return;
            setTableData(prev =>
              prev.map(r =>
                r.id === row.id ? { ...r, warehouseId: wh.id } : r
              )
            );
          }}
          className="p-inputtext-sm"
          style={{ width: '9rem' }}
          panelStyle={{ width: '9rem' }}
        />
      </div>
    );
  };

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);

    setTableData((prev) =>
      prev.map((row) => {
        const maxRemain = getRemainingQty(row);
        return {
          ...row,
          quantity: checked ? maxRemain : 0,
        };
      })
    );
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div className="flex justify-content-end align-items-center mb-2">
        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="order-pickup-select-all"
            checked={selectAll}
            onChange={(e) => handleSelectAllChange(!!e.checked)}
          />
          <label htmlFor="order-pickup-select-all" className="mb-0">
            {th('parts.receivedAll')}
          </label>
        </div>
      </div>
      <DataTable
        value={tableData}
        dataKey="id"
        className="p-datatable-gridlines"
        showGridlines
        style={{ minWidth: 700 }}
        emptyMessage={th('parts.emptyMessage')}
      >
        <Column field="partCode" header={th('product.code')} bodyClassName="py-2" />
        <Column field="partDescreption" header={th('product.name')} bodyClassName="py-2" />
        <Column field="shelf" header={th('parts.shelf')} bodyClassName="py-2" />
        <Column field="eyes" header={th('parts.eyes')} bodyClassName="py-2" />
        {!hidePriceColumns && (
          <Column
            field="unitPrice"
            header={th('product.price')}
            bodyClassName="py-2"
          />
        )}
        <Column field="totalQuantity" header={th('parts.purchased')} bodyClassName="py-2" />
        <Column field="unit" header={th('product.unit')} bodyClassName="py-2" />
       {!hidePriceColumns && (
          <Column
            field="totalAmount"
            header={th('parts.amount')}
            bodyClassName="py-2"
          />
        )}
        <Column field="receivedQuantity" header={th('parts.receivedDone')} bodyClassName="py-2" />
        <Column
          field="quantity"
          header={th('parts.receivedInput')}
          body={QtyCell}
          bodyClassName="py-2"
          style={{ width: 160, textAlign: 'center' }}
        />
        <Column
          field="warehouseId"
          header={th('dealerWarehouse.warehouseName')}
          body={WarehouseCell}
          bodyClassName="py-2"
          style={{ width: 170, textAlign: 'center' }}
        />
        <Column
          header={th('parts.receivedBy')}
          body={DetailCell}
          bodyClassName="py-2"
          style={{ width: 80, textAlign: 'center' }}
        />
      </DataTable>

      <Dialog
        header={th('parts.pickupDetail')}
        visible={detailDialogVisible}
        onHide={() => setDetailDialogVisible(false)}
        style={{ width: 560 }}
        modal
      >
        <DataTable
          value={detailData}
          dataKey="createdDate"
          className="p-datatable-gridlines"
          showGridlines
          loading={isFetchingDetail}
          emptyMessage={th('parts.emptyMessage')}
        >
          <Column field="userName" header={th('parts.receivedBy')} />
          <Column field="createdDate" header={th('parts.receivedDate')} />
          <Column field="quantity" header={th('parts.quantity')} />
        </DataTable>
      </Dialog>
    </div>
  );
}
